import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AccessType, RegistrationSource } from '@ffd/api-client';
import { pathParameter } from '@libs/shared-services';
import { authActions, selectCurrentUserProfile, selectIsUserLoggedIn, selectIsUserLoggedOut, selectRecentRealSiteId } from '@libs/store';
import { ffdNotesRoutePrefix, isFfdNotesContext, routes } from '@libs/utils';
import { Store } from '@ngrx/store';
import { filter, map, Observable, take, tap, withLatestFrom } from 'rxjs';
import { Platform } from '@angular/cdk/platform';
import { isMobileOrTablet } from '@libs/ui-lib';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly router = inject(Router);
  private readonly store = inject(Store);
  private readonly platform = inject(Platform);

  public isLoggedIn$(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.store.select(selectIsUserLoggedIn).pipe(
      tap((isLoggedIn) => {
        if (!isLoggedIn) {
          this.store.dispatch(authActions.onProfileRequested());
        }
      }),
      filter((loggedIn): loggedIn is boolean => loggedIn !== null),
      // eslint-disable-next-line sonarjs/cognitive-complexity
      map((isLoggedIn) => {
        if (!isLoggedIn) {
          const isSetupFlow = state.url.includes(routes.setup);
          const isSiteAdminFlow = state.url.includes(routes.siteAdmin);

          // Billing link sent to customers carries ?allowMobile=true: let mobile users reach the
          // login screen (and afterwards the billing tab) instead of the blocker. Scoped to billing.
          // Propagated into the login queryParams so MobileGuard lets the login screen through too.
          const isBillingMobileBypass = next.queryParams['allowMobile'] === 'true' && state.url.includes(`/${routes.billing}`);

          const queryParams = {
            redirectUrl: state.url,
            isSetupFlow: isSetupFlow ? true : undefined,
            isSiteAdminFlow: isSiteAdminFlow ? true : undefined,
            allowMobile: isBillingMobileBypass ? true : undefined,
            email: next.queryParams['email'],
            ffdNotes: next.queryParams['ffdNotes'],
          };

          const isFfdNotesUrl = isFfdNotesContext(state.url);

          if (isMobileOrTablet(this.platform) && !next.paramMap.has(pathParameter('siteId')) && !isBillingMobileBypass && !isSetupFlow) {
            this.router.navigate(isFfdNotesUrl ? [...ffdNotesRoutePrefix(), routes.getTheApp] : [routes.getTheApp]);
            return false;
          }

          // The default setup login screen is a desktop-only design, so phones fall through to the
          // standard login screen; redirectUrl still brings them back into /setup afterwards.
          // The FFD Notes variant (?ffdNotes=true) is responsive, so it is served on every device.
          const isFfdNotesSetup = next.queryParams['ffdNotes'] === 'true';
          if (state.url.split('?')[0] === `/${routes.setup}` && (isFfdNotesSetup || !isMobileOrTablet(this.platform))) {
            this.router.navigate([routes.login, routes.loginSetup], { queryParams: queryParams });
          } else if (isFfdNotesUrl) {
            this.router.navigate([...ffdNotesRoutePrefix(), routes.login], { queryParams: queryParams });
          } else {
            this.router.navigate([routes.login], { queryParams: queryParams });
          }

          return false;
        }
        return true;
      }),
    );
  }

  public isLoggedOut$(): Observable<boolean> {
    return this.store.select(selectIsUserLoggedOut).pipe(
      filter((loggedOut): loggedOut is boolean => loggedOut !== null),
      map((isLoggedOut) => {
        if (!isLoggedOut) {
          this.router.navigate([routes.site]);
          return false;
        }
        return true;
      }),
    );
  }

  public hasSiteAccess$(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // Wait for the profile, not for a non-empty site list: a user without any site (e.g. a deactivated patient)
    // still has to get through instead of hanging here.
    return this.store.select(selectCurrentUserProfile).pipe(
      filter((profile): profile is NonNullable<typeof profile> => !!profile),
      map(({ sites }) => {
        const siteId = next.paramMap.get(pathParameter('siteId'));
        const site = sites.find((site) => site.id === siteId);

        const isUserExternalProviderOrPatient =
          site?.provider?.accessType === AccessType.COLLAB_ONLY_ACCESS ||
          site?.staff?.accessType === AccessType.COLLAB_ONLY_ACCESS ||
          !!site?.nonPmsUser ||
          !!site?.patient;

        if (isUserExternalProviderOrPatient) {
          const nextRoute = routes.allCollaborationsOnSite(siteId!);

          // we navigate to a child route and this prevents the guard to trigger indefinitely.
          // Only the path is compared: the url carries query params (e.g. the post sign-up siteAssocSuccess flag) and
          // comparing the whole url would re-navigate and drop them.
          if (state.url.split('?')[0] === `/${nextRoute}`) {
            return true;
          }

          this.router.navigate([nextRoute], { queryParamsHandling: 'preserve' });
          return false;
        }

        return true;
      }),
    );
  }

  public hasNoSites$(): Observable<boolean> {
    return this.store.select(selectCurrentUserProfile).pipe(
      filter((profile): profile is NonNullable<typeof profile> => !!profile),
      map(({ sites }) => {
        if (sites.length > 0) {
          this.router.navigate([routes.site], { queryParamsHandling: 'preserve' });
          return false;
        }

        return true;
      }),
    );
  }

  public redirectToDefaultSite$(next: ActivatedRouteSnapshot): Observable<boolean> {
    return this.store.select(selectCurrentUserProfile).pipe(
      filter((profile) => !!profile),
      withLatestFrom(this.store.select(selectRecentRealSiteId)),
      tap(([profile, recentRealSiteId]) => {
        const { showcaseSiteId, site, allCollaborations } = routes;

        const recentRealSiteIdInSites = profile!.sites.find(
          (site) =>
            site.id === recentRealSiteId &&
            (site.provider?.accessType === AccessType.FULL_ACCESS || site.staff?.accessType === AccessType.FULL_ACCESS),
        )?.id;
        const firstInternalSite = profile!.sites.find(
          (s) =>
            (s.provider?.accessType === AccessType.FULL_ACCESS || s.staff?.accessType === AccessType.FULL_ACCESS) &&
            s.id !== showcaseSiteId,
        );
        const isSiteAssociationInProgress = next.queryParams['siteAssociationToken'];

        // if the recent site is stored in the local storage, we default to that site if it exists
        // if not, we default to the first internal site, if it exists
        const defaultSiteId =
          recentRealSiteIdInSites ??
          (firstInternalSite && !isSiteAssociationInProgress
            ? firstInternalSite.id
            : profile!.sites.find((site) => site.id !== showcaseSiteId)?.id);

        // a user without any site cannot use the showcase, so they go to the site-less collab screen
        if (profile!.sites.length === 0) {
          this.router.navigate([allCollaborations], { queryParamsHandling: 'preserve' });
          return;
        }

        this.router.navigate([site, defaultSiteId ?? showcaseSiteId], { queryParamsHandling: 'preserve' });
      }),
      map(() => true),
    );
  }

  public isGlobalAdmin$ = (): Observable<boolean> => {
    // Wait for the profile to load before deciding — on a hard refresh of /ffd-admin the store starts empty,
    // and evaluating a null profile would wrongly redirect a global admin away to the default site.
    return this.store.select(selectCurrentUserProfile).pipe(
      filter((user): user is NonNullable<typeof user> => !!user),
      take(1),
      map((user) => {
        if (user.isGlobalAdmin) {
          return true;
        }
        this.router.navigate(['/']);
        return false;
      }),
    );
  };

  public ffdCoreGuard$ = (): Observable<boolean> => {
    const store = inject(Store);
    const router = inject(Router);

    return store.select(selectCurrentUserProfile).pipe(
      filter((profile) => !!profile),
      take(1),
      map((profile) => {
        if (profile?.registrationSource === RegistrationSource.FFD_CORE) {
          return true;
        }

        // If not FFD Core, redirect to default site route
        router.navigate(ffdNotesRoutePrefix());
        return false;
      }),
    );
  };

  public ffdExpressGuard$ = (): Observable<boolean> => {
    const store = inject(Store);
    const router = inject(Router);

    return store.select(selectCurrentUserProfile).pipe(
      filter((profile) => !!profile),
      take(1),
      map((profile) => {
        if (profile?.registrationSource === RegistrationSource.FFD_EXPRESS) {
          return true;
        }

        // If not FFD Express, redirect to default site route
        router.navigate([routes.site]);
        return false;
      }),
    );
  };
}
